import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Have a question or feedback? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <a 
              href="mailto:tb930654@gmail.com"
              className="block"
            >
              <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full border-2 border-transparent hover:border-slate-300">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-slate-200 transition-colors">
                    <Mail className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
                  <p className="text-sm text-slate-700 hover:text-slate-900 font-medium">
                    tb930654@gmail.com
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Click to send email</p>
                </CardContent>
              </Card>
            </a>

            <a 
              href="https://www.google.com/maps/search/?api=1&query=FLAT+647+POCKET+C4+SECTOR+34+ROHINI+DELHI+110039+India"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full border-2 border-transparent hover:border-slate-300">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Address</h3>
                  <p className="text-sm text-slate-700 hover:text-slate-900 font-medium">
                    FLAT 647, POCKET C4, SECTOR 34,<br />
                    ROHINI, DELHI - 110039, India
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Click to open maps</p>
                </CardContent>
              </Card>
            </a>
          </div>

          <Card className="max-w-3xl mx-auto">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Subject *
                  </label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help you?"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us more about your inquiry..."
                    rows={6}
                    className="w-full resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Business Information */}
          <Card className="max-w-3xl mx-auto mt-8">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Business Information</h2>
              <div className="text-slate-600 space-y-1">
                <p><strong>Legal Name:</strong> ASHISH KUMAR</p>
                <p><strong>Developer:</strong> ivas.inc</p>
                <p><strong>Address:</strong> FLAT 647, POCKET C4, SECTOR 34, ROHINI, DELHI - 110039, India (IN)</p>
                <p><strong>Support Email:</strong> <a href="mailto:tb930654@gmail.com" className="text-slate-900 hover:underline">tb930654@gmail.com</a></p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
